import { useState, useRef } from 'react';
import NoPhiNotice from './NoPhiNotice';
import { httpsCallable } from '../lib/callable';
import { Upload, FileSpreadsheet, Loader2, CheckCircle2, AlertTriangle, X, Info } from 'lucide-react';
import { functions } from '../firebase';
import { CLOUD_FUNCTIONS, CALLABLE_TIMEOUTS } from '../constants/firebase';
import { useToast } from '../contexts/ToastContext';
import ConfirmDialog from './ConfirmDialog';
import {
  initialAccepted, splitReviewRows, declinedCount, needsDeclineConfirm, declineConfirmText,
} from '../features/dataManagement/adoptReviewDefaults';
import { adoptFormLabel } from '../features/dataManagement/adoptFormLabel';
import { adoptApplyState } from '../features/dataManagement/adoptApplyState';
import {
  groupColumnReport,
  columnReportSummary,
  shouldOpenColumnReport,
  formatValueCount,
  type ColumnReportRow,
} from '../features/dataManagement/columnReportDisplay';
import {
  redcapNoticeText,
  redcapNotTranslatedLine,
  type RedcapNotice,
} from '../features/dataManagement/redcapNoticeDisplay';
import {
  droppedEntriesText,
  droppedEntryLine,
  initialDroppedTicks,
  tickedDroppedIds,
  type DroppedEntry,
} from '../features/dataManagement/droppedEntriesDisplay';
import {
  initialSectionTicks,
  tickedKeys,
  sectionKey,
  reorderLine,
  repeatLine,
  unplacedLine,
  crossSectionLine,
  type ReorderProposal,
  type RepeatBlockProposal,
  type RepeatUnplacedReport,
  type CrossSectionReport,
} from '../features/dataManagement/formProposalsDisplay';
import {
  initialNewFormTicks,
  tickedNewForms,
  newFormLine,
  type NewFormProposal,
} from '../features/dataManagement/newFormProposals';
import { adoptStorageLines } from '../features/dataManagement/adoptStorageSummaryText';

/**
 * Apply Dictionary Edits — upload an EDITED copy of THIS study's data
 * dictionary export and apply the edits to the forms and the dictionary.
 *
 * NOT "Import DD". That button (CRF Builder) brings CRFs INTO a project from a
 * REDCap data dictionary and creates forms. This one changes the study that
 * already exists, and the server refuses a REDCap file, a foreign study's file,
 * and any export produced before the identity columns were added. The names,
 * the subtitles and the icons are deliberately different so the two cannot be
 * mistaken for each other in a hurry.
 *
 * Plan first, apply second: nothing is written until the user has seen every
 * row and pressed Apply, and Apply is disabled unless the server says the plan
 * is applyable.
 */

interface PlanChange {
  column: string;
  current: string;
  proposed: string;
  appliesTo: 'crf+entry' | 'entry';
  note?: string;
}
interface PlanRow {
  rowNumber: number;
  crfQuestionId: string;
  entryId: string;
  formCode: string;
  fieldName: string;
  status: 'change' | 'unchanged' | 'rejected';
  changes: PlanChange[];
  rejection?: { code: string; message: string };
}
interface ImportPlan {
  applyable: boolean;
  planId: string;
  totals: { rows: number; changed: number; unchanged: number; rejected: number };
  rows: PlanRow[];
}
interface StaleDownstream { document: string; reason: string; regenerateWith: string }
interface ApplyResult {
  applied: boolean;
  refusedReason?: string;
  rowsApplied?: number;
  entriesUpdated?: number;
  formsUpdated?: string[];
  staleDownstream?: StaleDownstream[];
}

/**
 * ADOPT rows carry different fields from round-trip rows: no entry id, a
 * `matchedBy`, and the question's current variable. Kept as its own shape
 * rather than widened into one union, so a field that only exists in one mode
 * cannot be read in the other by accident.
 */
interface AdoptChange { column: string; current: string; proposed: string; target: 'crf' | 'none'; note?: string }
type AdoptDisposition = 'apply' | 'review' | 'add' | 'unchanged' | 'rejected';
type MatchConfidence = 'exact' | 'high' | 'medium';
interface AdoptRow {
  rowNumber: number; formCode: string; projectFormCode?: string;
  /** The form the row resolved to, by identity. Absent from a plan made by an older function. */
  projectFormId?: string; projectFormName?: string;
  formResolvedBy?: 'code' | 'form_name' | 'form_name_similarity';
  fieldName: string; fieldLabel: string;
  crfQuestionId?: string; currentVariable?: string; currentText?: string;
  matchedBy?: 'field_name' | 'field_label' | 'normalised_field_name'
    | 'normalised_field_label' | 'anchored_position' | 'partner' | 'position_similarity';
  /** Why a position, partner or merge proposal was made. */
  reason?: string;
  /** A merge: the twin an earlier adoption added, removed when this row is applied. */
  mergesWith?: string;
  /** The row names a codelist the CDISC catalog does not hold. */
  sponsorCodelist?: boolean;
  confidence?: MatchConfidence;
  matchScore?: number;
  disposition: AdoptDisposition;
  changes: AdoptChange[];
  addToFormId?: string;
  notes?: string[];
  /** Review rows only: may start ticked (word for word, unique on the form). */
  defaultAccept?: boolean;
  /** Review rows only: the label appears more than once on the form. */
  repeatedOnForm?: boolean;
  rejection?: { code: string; message: string };
}
interface UntouchedQuestion {
  crfQuestionId: string; formCode: string; formId?: string; formName?: string; variable: string; text: string;
}
interface AdoptionPlan {
  applyable: boolean; planId: string;
  /**
   * Informational since 2026-09-15: `ok` is always true. `message` says what
   * adopting does to the documents this study already has. `outOfDate` names
   * the ones that need regenerating - never the dictionary, which adoption
   * rewrites. Both are absent from a plan made by an older function.
   */
  precondition: { ok: boolean; outOfDate?: string[]; message?: string };
  totals: {
    rows: number; accepted: number; needsReview: number; toAdd: number;
    unchanged: number; rejected: number; unclaimedQuestions: number; renames: number;
    /** Absent from a plan made by an older function. */
    merges?: number;
    sponsorCodelists?: number;
    codelistChanges?: number;
    reorders?: number;
    repeatBlocks?: number;
  };
  rows: AdoptRow[];
  untouched: UntouchedQuestion[];
  /** Per form: questions the dictionary does not mention; licensed instruments named. */
  columnReport?: ColumnReportRow[];
  /** Present only when the uploaded file was a REDCap export (D6). */
  redcap?: RedcapNotice;
  /** Rows this study's dictionary has that the file does not (2026-09-17). */
  dropped?: DroppedEntry[];
  /** D2: form-level proposals and reports. Absent from a plan made by an older function. */
  reorders?: ReorderProposal[];
  repeatBlocks?: RepeatBlockProposal[];
  /** Forms the file names that this project does not have (2026-09-20). */
  newForms?: NewFormProposal[];
  repeatUnplaced?: RepeatUnplacedReport[];
  crossSectionOrder?: CrossSectionReport[];
  formFlags?: Array<{
    formId: string; formName: string; formCode: string;
    notInDictionary: number; notInDictionaryVariables: string[]; licensedInstrument?: string;
  }>;
}
interface AdoptResult {
  applied: boolean; refusedReason?: string; rowsApplied?: number; questionsAdded?: number;
  /** D2: sections reordered / marked as repeating. Absent from an older function. */
  reordersApplied?: number; repeatBlocksApplied?: number;
  formsUpdated?: string[];
  renames?: Array<{ from: string; to: string; crfQuestionId: string; formCode: string }>;
  /** Dictionary entries stored from the file. Absent from an older function. */
  entriesStored?: number;
  /** Rows of the file not stored, entries stored without a link, and the forms
   *  those belong to (2026-10-01). Absent from an older function. */
  entriesNotStored?: number;
  entriesUnlinked?: number;
  unlinkedForms?: Array<{ formName: string; entries: number; formFound: boolean }>;
  notStoredReasons?: { notChosen: number; neededReview: number; couldNotBeUsed: number };
  /** Entries created from form questions the file does not cover, and forms that could not (2026-10-10). */
  entriesDerivedFromForms?: number;
  derivedBlockedForms?: Array<{ formName: string; reason: string }>;
  notApplied?: {
    needsReviewDeclined: number; rejected: number; unchanged: number;
    skipped: number; unclaimedQuestions: number;
  };
}

/** How a match was made, in words a reader can act on. */
const MATCHED_BY_LABEL: Record<string, string> = {
  field_name: 'variable name',
  field_label: 'label',
  normalised_field_name: 'variable name, ignoring case and punctuation',
  normalised_field_label: 'label, ignoring numbering and punctuation',
  anchored_position: 'position between questions that matched',
  partner: 'its partner field',
  position_similarity: 'position in the form and similar wording',
};

/**
 * Two modes, one modal, deliberately.
 *
 *   'roundtrip' — THIS study's own export comes back. Matched on the ids the
 *                 export carried; renames refused.
 *   'adopt'     — a FOREIGN dictionary becomes the source of truth for draft
 *                 forms. Ids ignored, matched on form_code + field_name then
 *                 label; renames ALLOWED. Existing downstream documents are
 *                 named as out of date, never a refusal (decision 7).
 *
 * Sharing the shell keeps plan-then-apply, all-or-nothing and the rejection
 * table identical in both, which is what a user needs to be able to rely on.
 * Everything that differs is named in COPY below rather than inferred.
 */
export type DictionaryUploadMode = 'roundtrip' | 'adopt';

/**
 * The documents an adoption does NOT rewrite, as the server names them in
 * `precondition.outOfDate`. The dictionary is deliberately absent: adoption
 * writes it, so it is never out of date afterwards.
 */
const OUT_OF_DATE_LABELS: Record<string, string> = {
  sdtmMapping: 'the SDTM mapping',
  editCheckSpec: 'the edit check specification',
  annotatedCrf: 'the annotated CRF',
};

/** "a", "a and b", "a, b and c" - the server's order, kept. */
function joinOutOfDate(keys: string[]): string {
  const names = keys.map((k) => OUT_OF_DATE_LABELS[k] || k);
  if (names.length <= 1) return names[0] || '';
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

const COPY: Record<DictionaryUploadMode, {
  title: string; subtitle: string; cta: string; blurb: JSX.Element;
}> = {
  roundtrip: {
    title: 'Apply Dictionary Edits',
    subtitle: "Upload the data dictionary you exported from this study after editing it, and apply those edits to the forms and the dictionary.",
    cta: 'Check this file',
    blurb: (
      <>
        <p>
          You can change <strong>label, field type, picklist values, help text</strong> and{' '}
          <strong>required</strong>. Leave every other column exactly as exported — including the
          two id columns at the end, which is how each row is matched to its question.
        </p>
        <p className="mt-1">
          <strong>Renaming a variable is not supported here.</strong> The variable name is the key
          used by the SDTM mapping, edit checks, the annotated CRF and the ODM export, so a changed
          <code className="mx-1 rounded bg-blue-100 px-1">field_name</code> is reported and nothing
          is applied.
        </p>
      </>
    ),
  },
  adopt: {
    title: 'Adopt Dictionary as Source of Truth',
    subtitle: 'Upload a DocumentSpark data dictionary from another project and make these forms match it. For a new project whose forms came from a packet upload.',
    cta: 'Check this dictionary',
    blurb: (
      <>
        <p>
          The dictionary is the authority: variable names, labels, field types and picklist values
          on the forms are changed to match it. Rows are reconciled against the forms in stages —
          exact match, then ignoring case, numbering and punctuation, then by position and
          wording — and each row is shown with how confident that match is. Any id columns in the
          file are <strong>ignored</strong> — they belong to the project it came from.
        </p>
        <p className="mt-1">
          Confident matches apply. Anything matched only by position and wording is listed for
          you to accept or reject. A row that matches nothing becomes a <strong>new question</strong>{' '}
          on the form, and a question with no row is <strong>kept and flagged</strong>, never deleted.
        </p>
        <p className="mt-1">
          <strong>Renaming a variable IS allowed here.</strong> The stored data dictionary takes
          the file&apos;s rows. If this study already has an SDTM mapping, edit checks or an
          annotated CRF, they are built on the old names; each will show a notice naming the
          renamed variables, where you choose how to bring it up to date.
        </p>
      </>
    ),
  },
};

interface Props {
  projectId: string;
  /** Which mode this modal is running. Defaults to the round trip. */
  mode?: DictionaryUploadMode;
  onClose: () => void;
  onApplied?: () => void;
}

type Stage = 'picking' | 'planning' | 'review' | 'applying' | 'done' | 'error';

/**
 * How many rows of any one section render before the section offers to expand.
 * A real dictionary runs to 1,591 rows against 1,532 questions; rendering all
 * of them at once is what makes the modal feel broken.
 */
const ROW_CAP = 100;

const COLUMN_LABELS: Record<string, string> = {
  field_label: 'Label',
  field_type: 'Field type',
  picklist_values: 'Picklist values',
  codelist_name: 'Codelist',
  help_text: 'Help text',
  required: 'Required',
  control_type: 'Answer type',
};

export default function ApplyDictionaryEditsModal({ projectId, mode = 'roundtrip', onClose, onApplied }: Props) {
  const copy = COPY[mode];
  const isAdopt = mode === 'adopt';
  const [stage, setStage] = useState<Stage>('picking');
  const [file, setFile] = useState<File | null>(null);
  const [csvText, setCsvText] = useState('');
  const [plan, setPlan] = useState<ImportPlan | null>(null);
  const [adoptPlan, setAdoptPlan] = useState<AdoptionPlan | null>(null);
  /** null = follow the report's own recommendation; a boolean = the user chose. */
  const [columnsOpen, setColumnsOpen] = useState<boolean | null>(null);
  /** D2: reorders and repeating blocks, by section key. All start ticked. */
  const [reorderTicks, setReorderTicks] = useState<Record<string, boolean>>({});
  const [droppedTicks, setDroppedTicks] = useState<Record<string, boolean>>({});
  const [repeatTicks, setRepeatTicks] = useState<Record<string, boolean>>({});
  /** Forms the file names that this project does not have (2026-09-20). */
  const [newFormTicks, setNewFormTicks] = useState<Record<string, boolean>>({});
  const [adoptResult, setAdoptResult] = useState<AdoptResult | null>(null);
  const [result, setResult] = useState<ApplyResult | null>(null);
  const [error, setError] = useState('');
  const [dragActive, setDragActive] = useState(false);
  /** Medium-confidence rows the user ticked. Empty by default: a guess never
   *  applies unattended, and a rename is not something to opt out of. */
  const [accepted, setAccepted] = useState<Record<number, boolean>>({});
  /** Rows the user turned OFF — applies to confident matches and to adds. */
  const [skipped, setSkipped] = useState<Record<number, boolean>>({});
  /** Sections start collapsed when long, so 1,500 rows do not land at once. */
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  /** Adopt pressed with proposals still unticked: ask before declining them. */
  const [confirmDecline, setConfirmDecline] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { showToast } = useToast();

  const pick = (picked: File) => {
    if (!picked.name.toLowerCase().endsWith('.csv')) {
      showToast('Upload the .csv you downloaded with Download Data Dictionary.', 'error');
      return;
    }
    setFile(picked);
    setError('');
  };

  const runPlan = async () => {
    if (!file) return;
    setError('');
    setStage('planning');
    try {
      const text = await file.text();
      setCsvText(text);
      const fn = httpsCallable(
        functions,
        isAdopt ? CLOUD_FUNCTIONS.PLAN_DICTIONARY_ADOPTION : CLOUD_FUNCTIONS.PLAN_DICTIONARY_IMPORT,
        { timeout: CALLABLE_TIMEOUTS.MEDIUM },
      );
      const res = await fn({ projectId, csvText: text });
      if (isAdopt) setAdoptPlan(res.data as AdoptionPlan);
      else setPlan(res.data as ImportPlan);
      // A new plan invalidates every decision made against the previous one.
      // Start from the planner's defaults, not from nothing: an empty
      // selection is what let one press of Adopt decline 509 proposals.
      setAccepted(initialAccepted(isAdopt ? ((res.data as AdoptionPlan).rows || []) : []));
      if (isAdopt) {
        setReorderTicks(initialSectionTicks((res.data as AdoptionPlan).reorders));
        setRepeatTicks(initialSectionTicks((res.data as AdoptionPlan).repeatBlocks));
        setNewFormTicks(initialNewFormTicks((res.data as AdoptionPlan).newForms));
      }
      setSkipped({});
      setExpanded({});
      setStage('review');
    } catch (err: any) {
      // The server's refusal message is the useful part — it names the reason
      // and what to do — so it is shown verbatim rather than replaced.
      setError(err?.message || 'Could not read that file.');
      setStage('error');
    }
  };

  // Adopt with proposals still unticked declines them silently - on the FOG
  // project that was 509 at once. Ask first, and name the count.
  const requestApply = () => {
    if (isAdopt && needsDeclineConfirm(declinedOnAdopt)) { setConfirmDecline(true); return; }
    void runApply();
  };

  const runApply = async () => {
    const active = isAdopt ? adoptPlan : plan;
    if (!active) return;
    if (isAdopt ? !adoptApplyable : !active.applyable) return;
    setStage('applying');
    try {
      const fn = httpsCallable(
        functions,
        isAdopt ? CLOUD_FUNCTIONS.APPLY_DICTIONARY_ADOPTION : CLOUD_FUNCTIONS.APPLY_DICTIONARY_IMPORT,
        { timeout: CALLABLE_TIMEOUTS.LONG },
      );
      const res = await fn({
        projectId, csvText, planId: active.planId,
        ...(isAdopt ? {
          acceptedRows: reviewRows.filter((r) => accepted[r.rowNumber]).map((r) => r.rowNumber),
          skippedRows: Object.keys(skipped).filter((k) => skipped[Number(k)]).map(Number),
          // Always sent, even empty: the server applies no reorder or repeat mark
          // when a list is ABSENT, which is how an older page is kept safe.
          acceptedReorders: tickedKeys(adoptPlan?.reorders, reorderTicks),
          acceptedRepeatBlocks: tickedKeys(adoptPlan?.repeatBlocks, repeatTicks),
          // Also always sent: absent means "remove none", so an older page
          // cannot drop a row nobody was shown (2026-09-17).
          acceptedDroppedEntryIds: tickedDroppedIds(droppedTicks),
          // Always sent too: absent means "create none", which is what keeps a
          // page that predates new-form proposals from creating forms.
          acceptedNewForms: tickedNewForms(adoptPlan?.newForms, newFormTicks),
        } : {}),
      });
      const data = res.data as ApplyResult & AdoptResult;
      if (isAdopt) setAdoptResult(data as AdoptResult); else setResult(data as ApplyResult);
      if (!data.applied) {
        setError(data.refusedReason || 'Nothing was applied.');
        setStage('error');
        return;
      }
      setStage('done');
      showToast(
        isAdopt
          ? ((data.formsUpdated || []).length > 0
            ? `Adopted the dictionary onto ${(data.formsUpdated || []).length} form(s): ` +
              `${data.rowsApplied || 0} row(s), ${data.questionsAdded || 0} added` +
              (data.reordersApplied ? `, ${data.reordersApplied} section(s) reordered` : '') +
              (data.repeatBlocksApplied ? `, ${data.repeatBlocksApplied} repeating block(s) marked` : '') +
              '.'
            : `Stored the dictionary (${data.entriesStored ?? 0} entries). No form was changed.`)
          : `Applied ${data.rowsApplied} data dictionary edit(s).`,
        'success',
      );
      onApplied?.();
    } catch (err: any) {
      setError(err?.message || 'Failed to apply the edits.');
      setStage('error');
    }
  };

  const activePlan: { applyable: boolean; totals: any; rows: any[] } | null =
    (isAdopt ? adoptPlan : plan) as any;
  // Round-trip rows carry `status`; adopt rows carry `disposition`. Read the
  // one that mode actually has rather than a union that is half undefined.
  const changed = isAdopt ? [] : (plan?.rows || []).filter((r) => r.status === 'change');
  const rejected: Array<PlanRow | AdoptRow> = isAdopt
    ? (adoptPlan?.rows || []).filter((r) => r.disposition === 'rejected')
    : (plan?.rows || []).filter((r) => r.status === 'rejected');
  const adoptRows = isAdopt ? (adoptPlan?.rows || []) : [];
  const autoRows = adoptRows.filter((r) => r.disposition === 'apply');
  const reviewRows = adoptRows.filter((r) => r.disposition === 'review');
  const reviewGroups = splitReviewRows(reviewRows);
  const declinedOnAdopt = declinedCount(reviewRows, accepted);
  const addRows = adoptRows.filter((r) => r.disposition === 'add');
  const selectedCount =
    autoRows.filter((r) => !skipped[r.rowNumber]).length +
    reviewRows.filter((r) => accepted[r.rowNumber] && !skipped[r.rowNumber]).length +
    addRows.filter((r) => !skipped[r.rowNumber]).length;
  // The server's plan carries the one rule for whether the file has anything to
  // adopt; the client only adds the reviewer's selection - a selected row, or
  // rows the forms already agree with, stored without changing a form.
  // Existing documents never disable this (decision 7).
  const adoptApply = adoptApplyState({
    planApplyable: !!adoptPlan?.applyable,
    selectedCount,
    unchangedCount: adoptRows.filter((r) => r.disposition === 'unchanged').length,
    sectionChangeCount:
      tickedKeys(adoptPlan?.reorders, reorderTicks).length +
      tickedKeys(adoptPlan?.repeatBlocks, repeatTicks).length,
  });
  const adoptApplyable = adoptApply.enabled;
  const busy = stage === 'planning' || stage === 'applying';

  // One amber block per group of proposals. Two groups: the ordinary ones, and
  // the ones in repeated blocks, which a person has to read one by one.
  const renderReviewGroup = (
    rows: AdoptRow[], key: 'merges' | 'replacements' | 'review' | 'repeated', heading: string, explainer: string,
  ) => {
    if (rows.length === 0) return null;
    const tickAll = () => setAccepted((prev) => ({
      ...prev, ...Object.fromEntries(rows.map((r) => [r.rowNumber, true])),
    }));
    const tickNone = () => setAccepted((prev) => {
      const next = { ...prev };
      for (const r of rows) delete next[r.rowNumber];
      return next;
    });
    return (
      <div className="mb-5 rounded-lg border border-amber-200 bg-amber-50 p-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="flex items-center gap-2 text-sm font-semibold text-amber-900">
            <AlertTriangle className="h-4 w-4" />
            {heading}
          </p>
          <div className="flex gap-2">
            <button type="button" onClick={tickAll}
              className="rounded border border-amber-400 px-2 py-1 text-xs text-amber-900 hover:bg-amber-100">Accept all</button>
            <button type="button" onClick={tickNone}
              className="rounded border border-amber-400 px-2 py-1 text-xs text-amber-900 hover:bg-amber-100">Accept none</button>
          </div>
        </div>
        <p className="mt-1 text-xs text-amber-900/80">{explainer}</p>
        <table className="mt-3 w-full text-sm">
          <thead>
            <tr className="text-left text-amber-900/70">
              <th className="py-1 pr-2 font-medium w-8"></th>
              <th className="py-1 pr-3 font-medium">Row</th>
              <th className="py-1 pr-3 font-medium">Dictionary says</th>
              <th className="py-1 pr-3 font-medium">Proposed question</th>
              <th className="py-1 font-medium">Why</th>
            </tr>
          </thead>
          <tbody>
            {(expanded[key] ? rows : rows.slice(0, ROW_CAP)).map((r) => (
              <tr key={`${key}-${r.rowNumber}`} className="align-top border-t border-amber-200">
                <td className="py-2 pr-2">
                  <input
                    type="checkbox"
                    checked={!!accepted[r.rowNumber]}
                    onChange={(e) => setAccepted((prev) => ({ ...prev, [r.rowNumber]: e.target.checked }))}
                    aria-label={`Accept row ${r.rowNumber}`}
                  />
                </td>
                <td className="py-2 pr-3 tabular-nums text-amber-900">{r.rowNumber}</td>
                <td className="py-2 pr-3 text-amber-900">
                  <span className="font-mono text-xs">{r.fieldName}</span>
                  <div className="text-xs text-amber-900/80">{r.fieldLabel}</div>
                </td>
                <td className="py-2 pr-3 text-amber-900">
                  <span className="font-mono text-xs">{r.currentVariable}</span>
                  <div className="text-xs text-amber-900/80">{r.currentText}</div>
                </td>
                <td className="py-2 text-xs text-amber-900/80">
                  {MATCHED_BY_LABEL[r.matchedBy || ''] || r.matchedBy}
                  {r.matchScore !== undefined && ` (${Math.round(r.matchScore * 100)}%)`}
                  {r.reason && <div className="mt-0.5 italic">{r.reason}</div>}
                  {r.sponsorCodelist && <div className="mt-0.5">Codelist not in the CDISC catalog: stored as written</div>}
                  <div className="mt-0.5">
                    on form {adoptFormLabel({ name: r.projectFormName, code: r.projectFormCode })}
                    {r.formResolvedBy && r.formResolvedBy !== 'code' && ' - form matched by name'}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {rows.length > ROW_CAP && (
          <button type="button" onClick={() => setExpanded((prev) => ({ ...prev, [key]: !prev[key] }))}
            className="mt-2 text-xs font-medium text-amber-900 underline">
            {expanded[key] ? 'Show fewer' : `Show all ${rows.length}`}
          </button>
        )}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-4xl shadow-xl max-h-[90vh] flex flex-col">
        <div className="p-6 border-b border-gray-200 flex items-start justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">{copy.title}</h2>
            <p className="text-sm text-gray-500 mt-0.5">{copy.subtitle}</p>
          </div>
          <button onClick={onClose} disabled={busy} className="text-gray-400 hover:text-gray-600 disabled:opacity-50">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto flex-1">
          {stage === 'picking' && (
            <>
              <div className="mb-4 flex gap-2 rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900">
                <Info className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <div>{copy.blurb}</div>
              </div>
              <NoPhiNotice className="mb-4" />
              <div
                onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
                onDragLeave={() => setDragActive(false)}
                onDrop={(e) => { e.preventDefault(); setDragActive(false); const f = e.dataTransfer.files?.[0]; if (f) pick(f); }}
                onClick={() => fileInputRef.current?.click()}
                className={`border-2 border-dashed rounded-xl p-10 text-center cursor-pointer transition ${
                  dragActive ? 'border-primary-500 bg-primary-50' : 'border-gray-300 hover:border-gray-400'
                }`}
              >
                <FileSpreadsheet className="h-10 w-10 mx-auto text-gray-400" />
                <p className="mt-3 text-sm text-gray-700">
                  {file ? file.name : 'Drop the edited .csv here, or click to choose'}
                </p>
                <input
                  ref={fileInputRef} type="file" accept=".csv" className="hidden"
                  onChange={(e) => { const f = e.target.files?.[0]; if (e.target) e.target.value = ''; if (f) pick(f); }}
                />
              </div>
            </>
          )}

          {stage === 'planning' && (
            <div className="py-12 text-center text-gray-600">
              <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary-600" />
              <p className="mt-3 text-sm">Checking the file against this study…</p>
            </div>
          )}

          {stage === 'review' && activePlan && (
            <>
              {/* Documents that already exist go out of date when this is
                  adopted. A NOTICE, never a refusal (decision 7, 2026-09-15):
                  it blocks nothing, and it sends the user nowhere else. */}
              {isAdopt && adoptPlan && adoptPlan.precondition.message && (
                <div className="mb-5 rounded-lg border border-amber-200 bg-amber-50 p-4">
                  <p className="flex items-center gap-2 text-sm font-semibold text-amber-900">
                    <AlertTriangle className="h-4 w-4" /> What adopting changes
                  </p>
                  <p className="mt-2 text-sm text-amber-900">{adoptPlan.precondition.message}</p>
                </div>
              )}
              <div className="mb-4 flex flex-wrap gap-2 text-sm">
                <span className="rounded-full bg-gray-100 px-3 py-1">{activePlan.totals.rows} rows</span>
                {isAdopt ? (
                  <>
                    <span className="rounded-full bg-green-100 text-green-800 px-3 py-1">{adoptPlan!.totals.accepted} will apply</span>
                    {adoptPlan!.totals.needsReview > 0 && (
                      <span className="rounded-full bg-amber-100 text-amber-900 px-3 py-1">{adoptPlan!.totals.needsReview} need review</span>
                    )}
                    {adoptPlan!.totals.toAdd > 0 && (
                      <span className="rounded-full bg-blue-100 text-blue-900 px-3 py-1">{adoptPlan!.totals.toAdd} to add</span>
                    )}
                  </>
                ) : (
                  <span className="rounded-full bg-green-100 text-green-800 px-3 py-1">{activePlan.totals.changed} to change</span>
                )}
                <span className="rounded-full bg-gray-100 px-3 py-1">{activePlan.totals.unchanged} unchanged</span>
                {activePlan!.totals.rejected > 0 && (
                  <span className="rounded-full bg-red-100 text-red-800 px-3 py-1">{activePlan!.totals.rejected} cannot be placed</span>
                )}
                {isAdopt && (adoptPlan!.totals.sponsorCodelists ?? 0) > 0 && (
                  <span className="rounded-full bg-amber-100 text-amber-900 px-3 py-1">{adoptPlan!.totals.sponsorCodelists} codelist(s) not in the CDISC catalog</span>
                )}
                {isAdopt && (adoptPlan!.totals.merges ?? 0) > 0 && (
                  <span className="rounded-full bg-amber-100 text-amber-900 px-3 py-1">{adoptPlan!.totals.merges} merge(s)</span>
                )}
                {isAdopt && adoptPlan!.totals.renames > 0 && (
                  <span className="rounded-full bg-amber-100 text-amber-900 px-3 py-1">{adoptPlan!.totals.renames} rename(s)</span>
                )}
                {isAdopt && adoptPlan!.totals.unclaimedQuestions > 0 && (
                  <span className="rounded-full bg-gray-100 px-3 py-1">{adoptPlan!.totals.unclaimedQuestions} question(s) not in the file</span>
                )}
              </div>

              {/* ── Adopt: rows this file no longer has (2026-09-17) ── */}
              {isAdopt && (adoptPlan!.dropped || []).length > 0 && (() => {
                const dropped = adoptPlan!.dropped!;
                const text = droppedEntriesText(dropped)!;
                const ticks = Object.keys(droppedTicks).length
                  ? droppedTicks : initialDroppedTicks(dropped);
                return (
                  <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50/60 px-4 py-3 text-sm">
                    <p className="font-medium text-amber-900">{text.headline}</p>
                    <p className="mt-1 text-gray-700">{text.body}</p>
                    <ul className="mt-2 space-y-1">
                      {dropped.map((d) => (
                        <li key={d.docId} className="flex items-start gap-2 text-xs text-gray-700">
                          <input
                            type="checkbox"
                            className="mt-0.5"
                            checked={!!ticks[d.docId]}
                            onChange={(e) => setDroppedTicks({ ...ticks, [d.docId]: e.target.checked })}
                            aria-label={`Remove ${d.fieldName} from the dictionary`}
                          />
                          <span>{droppedEntryLine(d)}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })()}

              {/* ── Adopt: the file was a REDCap export (D6) ── */}
              {isAdopt && adoptPlan!.redcap && (() => {
                const notice = adoptPlan!.redcap!;
                const text = redcapNoticeText(notice);
                return (
                  <div className="mt-4 rounded-lg border border-sky-200 bg-sky-50/60 px-4 py-3 text-sm">
                    <p className="font-medium text-sky-900">{text.headline}</p>
                    {text.skipped && <p className="mt-1 text-gray-700">{text.skipped}</p>}
                    {text.notTranslated && (
                      <div className="mt-1">
                        <p className="text-amber-800">{text.notTranslated}</p>
                        <ul className="mt-1 ml-4 list-disc space-y-0.5 text-xs text-gray-700">
                          {notice.notTranslated.map((n, i) => (
                            <li key={`${n.form}|${n.field}|${n.column}|${i}`}>{redcapNotTranslatedLine(n)}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* ── Adopt: what happens to every column of the file (design D4) ── */}
              {isAdopt && (adoptPlan!.columnReport || []).length > 0 && (() => {
                const report = adoptPlan!.columnReport!;
                const open = columnsOpen ?? shouldOpenColumnReport(report);
                return (
                  <div className="mt-4 rounded-lg border border-gray-200">
                    <button
                      type="button"
                      onClick={() => setColumnsOpen(!open)}
                      className="flex w-full items-start gap-2 px-4 py-3 text-left hover:bg-gray-50"
                      aria-expanded={open}
                    >
                      <Info className="mt-0.5 h-4 w-4 flex-shrink-0 text-gray-500" />
                      <span className="flex-1 text-sm text-gray-700">
                        <span className="font-medium">What happens to each column of your file</span>
                        <span className="block text-gray-600">{columnReportSummary(report)}</span>
                      </span>
                      <span className="text-xs text-gray-500">{open ? 'Hide' : 'Show'}</span>
                    </button>
                    {open && (
                      <div className="border-t border-gray-200 px-4 py-3 space-y-4">
                        {groupColumnReport(report).map((group) => (
                          <div key={group.key}>
                            <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                              {group.heading}
                            </div>
                            <table className="mt-1 w-full text-sm">
                              <tbody>
                                {group.rows.map((row) => (
                                  <tr key={row.column} className="align-top">
                                    <td className="py-1 pr-3 font-mono text-xs text-gray-800">
                                      {row.column}
                                      {row.sponsorColumn && (
                                        <span className="ml-1 rounded bg-blue-50 px-1 text-[10px] font-sans text-blue-800">
                                          your column
                                        </span>
                                      )}
                                    </td>
                                    <td className="py-1 pr-3 whitespace-nowrap text-gray-600">
                                      {formatValueCount(row.valuesInFile)}
                                    </td>
                                    <td className="py-1 text-gray-600">
                                      {row.changesProposed > 0
                                        ? `${row.changesProposed} form change(s) proposed`
                                        : row.reason || ''}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* ── Adopt: forms this file names that the project does not have
                     (2026-09-20). Shown FIRST, above the layout proposals,
                     because on a study with no forms this is the whole of what
                     the adoption will do. ── */}
              {isAdopt && (adoptPlan!.newForms || []).length > 0 && (
                <div className="mt-4 rounded-lg border border-emerald-200 bg-emerald-50 p-4 space-y-2">
                  <p className="text-sm font-semibold text-emerald-900">
                    Forms to create from this dictionary
                  </p>
                  <p className="text-xs text-emerald-900">
                    This file names {(adoptPlan!.newForms || []).length} form
                    {(adoptPlan!.newForms || []).length === 1 ? '' : 's'} this study does not have.
                    They are created as drafts. Untick any you do not want.
                  </p>
                  <ul className="mt-1 space-y-1">
                    {(adoptPlan!.newForms || []).map((f) => (
                      <li key={`newform-${f.formCode}`} className="flex items-start gap-2 text-sm text-gray-800">
                        <input
                          type="checkbox"
                          className="mt-1"
                          checked={!!newFormTicks[f.formCode]}
                          onChange={(e) => setNewFormTicks((t) => ({ ...t, [f.formCode]: e.target.checked }))}
                          aria-label={newFormLine(f)}
                        />
                        <span>{newFormLine(f)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* ── Adopt: the dictionary's order and repeating blocks, per section (D2) ── */}
              {isAdopt && ((adoptPlan!.reorders || []).length > 0
                || (adoptPlan!.repeatBlocks || []).length > 0
                || (adoptPlan!.repeatUnplaced || []).length > 0
                || (adoptPlan!.crossSectionOrder || []).length > 0) && (
                <div className="mt-4 rounded-lg border border-blue-200 bg-blue-50 p-4 space-y-3">
                  <p className="text-sm font-semibold text-blue-900">Form layout from the dictionary</p>
                  <p className="text-xs text-blue-900">
                    Questions only move within their own section. Untick anything you want left as it is.
                  </p>
                  {(adoptPlan!.reorders || []).length > 0 && (
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wide text-blue-800">Question order</p>
                      <ul className="mt-1 space-y-1">
                        {(adoptPlan!.reorders || []).map((r) => {
                          const k = sectionKey(r);
                          return (
                            <li key={`reorder-${k}`} className="flex items-start gap-2 text-sm text-gray-800">
                              <input
                                type="checkbox"
                                className="mt-1"
                                checked={!!reorderTicks[k]}
                                onChange={(e) => setReorderTicks((t) => ({ ...t, [k]: e.target.checked }))}
                                aria-label={reorderLine(r)}
                              />
                              <span>{reorderLine(r)}</span>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  )}
                  {(adoptPlan!.repeatBlocks || []).length > 0 && (
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wide text-blue-800">Repeating blocks</p>
                      <ul className="mt-1 space-y-1">
                        {(adoptPlan!.repeatBlocks || []).map((b) => {
                          const k = sectionKey(b);
                          return (
                            <li key={`repeat-${k}`} className="flex items-start gap-2 text-sm text-gray-800">
                              <input
                                type="checkbox"
                                className="mt-1"
                                checked={!!repeatTicks[k]}
                                onChange={(e) => setRepeatTicks((t) => ({ ...t, [k]: e.target.checked }))}
                                aria-label={repeatLine(b)}
                              />
                              <span>{repeatLine(b)}</span>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  )}
                  {((adoptPlan!.repeatUnplaced || []).length > 0
                    || (adoptPlan!.crossSectionOrder || []).length > 0) && (
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wide text-gray-600">Left as they are</p>
                      <ul className="mt-1 list-disc pl-5 space-y-1 text-sm text-gray-700">
                        {(adoptPlan!.repeatUnplaced || []).map((u) => (
                          <li key={`unplaced-${u.formId}-${u.pageName}`}>{unplacedLine(u)}</li>
                        ))}
                        {(adoptPlan!.crossSectionOrder || []).map((c) => (
                          <li key={`cross-${c.formId}-${c.pageName}`}>{crossSectionLine(c)}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}

              {/* ── Adopt: proposals that need a person, in four groups (design §6) ── */}
              {isAdopt && renderReviewGroup(
                reviewGroups.merges, 'merges',
                `${reviewGroups.merges.length} question(s) an earlier adoption added beside the original it could not pair`,
                'Ticked: the original keeps its place and identity and takes the dictionary\'s name, wording, type and choices; the added copy is removed. Untick any that are not the same item.',
              )}
              {isAdopt && renderReviewGroup(
                reviewGroups.replacements, 'replacements',
                `${reviewGroups.replacements.length} row(s) matched by position between matched questions or by a partner field`,
                'Ticked: these are the pairings a person sees when the two lists are laid side by side. Untick any that are wrong.',
              )}
              {isAdopt && renderReviewGroup(
                reviewGroups.single, 'review',
                `${reviewGroups.single.length} row(s) matched only by position and wording`,
                'These are proposals, not certainties. Tick the ones that are right; any left unticked are declined when you adopt.',
              )}
              {isAdopt && renderReviewGroup(
                reviewGroups.repeated, 'repeated',
                `${reviewGroups.repeated.length} proposal(s) where the label appears more than once on the form`,
                'These labels appear more than once on the form - for example left and right lead, or wound 1 and wound 2 - so a proposal may point at the wrong copy. They start unticked. Check each one against the form.',
              )}

              {/* ── Adopt: rows that become new questions ── */}
              {isAdopt && addRows.length > 0 && (
                <div className="mb-5 rounded-lg border border-blue-200 bg-blue-50 p-4">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <p className="text-sm font-semibold text-blue-900">
                      {addRows.length} row(s) match no question, and will be ADDED to the form
                    </p>
                    <button
                      type="button"
                      onClick={() => setSkipped((prev) => {
                        const allSkipped = addRows.every((r) => prev[r.rowNumber]);
                        const next = { ...prev };
                        for (const r of addRows) next[r.rowNumber] = !allSkipped;
                        return next;
                      })}
                      className="rounded border border-blue-400 px-2 py-1 text-xs text-blue-900 hover:bg-blue-100"
                    >Add none / add all</button>
                  </div>
                  <p className="mt-1 text-xs text-blue-900/80">
                    Appended to the end of the form, so existing questions keep their place and their ids.
                  </p>
                  <table className="mt-3 w-full text-sm">
                    <thead>
                      <tr className="text-left text-blue-900/70">
                        <th className="py-1 pr-2 font-medium w-8"></th>
                        <th className="py-1 pr-3 font-medium">Row</th>
                        <th className="py-1 pr-3 font-medium">Form</th>
                        <th className="py-1 pr-3 font-medium">Variable</th>
                        <th className="py-1 font-medium">Label</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(expanded.adds ? addRows : addRows.slice(0, ROW_CAP)).map((r) => (
                        <tr key={`add-${r.rowNumber}`} className="align-top border-t border-blue-200">
                          <td className="py-2 pr-2">
                            <input
                              type="checkbox"
                              checked={!skipped[r.rowNumber]}
                              onChange={(e) => setSkipped((prev) => ({ ...prev, [r.rowNumber]: !e.target.checked }))}
                              aria-label={`Add row ${r.rowNumber}`}
                            />
                          </td>
                          <td className="py-2 pr-3 tabular-nums text-blue-900">{r.rowNumber}</td>
                          <td className="py-2 pr-3 text-xs text-blue-900">
                            {adoptFormLabel({ name: r.projectFormName, code: r.projectFormCode })}
                          </td>
                          <td className="py-2 pr-3 font-mono text-xs text-blue-900">{r.fieldName}</td>
                          <td className="py-2 text-blue-900">{r.fieldLabel}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {addRows.length > ROW_CAP && (
                    <button type="button" onClick={() => setExpanded((p) => ({ ...p, adds: !p.adds }))}
                      className="mt-2 text-xs font-medium text-blue-900 underline">
                      {expanded.adds ? 'Show fewer' : `Show all ${addRows.length}`}
                    </button>
                  )}
                </div>
              )}

              {/* ── Adopt: licensed instruments carrying items the dictionary does not mention (design §4) ── */}
              {isAdopt && (adoptPlan!.formFlags || []).some((f) => f.licensedInstrument) && (
                <div className="mb-5 rounded-lg border border-amber-200 bg-amber-50 p-4">
                  <p className="flex items-center gap-2 text-sm font-semibold text-amber-900">
                    <AlertTriangle className="h-4 w-4" />
                    Licensed instruments with items not in the dictionary
                  </p>
                  <ul className="mt-2 space-y-1 text-xs text-amber-900">
                    {(adoptPlan!.formFlags || []).filter((f) => f.licensedInstrument).map((f) => (
                      <li key={`lic-${f.formId}`}>
                        {adoptFormLabel({ name: f.formName, code: f.formCode })} ({f.licensedInstrument}): licensed instrument, {f.notInDictionary} of its items are not in the dictionary. They are kept; check the dictionary against the publisher's version.
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* ── Adopt: questions no row claimed. Kept, never deleted. ── */}
              {isAdopt && (adoptPlan!.untouched || []).length > 0 && (
                <div className="mb-5 rounded-lg border border-gray-200 bg-gray-50 p-4">
                  <p className="text-sm font-semibold text-gray-800">
                    {adoptPlan!.untouched.length} question(s) on the forms have no row in this dictionary
                  </p>
                  <p className="mt-1 text-xs text-gray-600">
                    They are kept exactly as they are. Flagged so you can decide whether the
                    dictionary is missing them or the forms carry something it does not.
                  </p>
                  <ul className="mt-2 space-y-0.5 text-xs text-gray-700">
                    {(adoptPlan!.formFlags || []).map((f) => (
                      <li key={`nid-${f.formId}`}>
                        {adoptFormLabel({ name: f.formName, code: f.formCode })}: {f.notInDictionary} not in the dictionary
                      </li>
                    ))}
                  </ul>
                  <button type="button" onClick={() => setExpanded((p) => ({ ...p, unclaimed: !p.unclaimed }))}
                    className="mt-2 text-xs font-medium text-gray-700 underline">
                    {expanded.unclaimed ? 'Hide' : 'List them'}
                  </button>
                  {expanded.unclaimed && (
                    <ul className="mt-3 space-y-1 text-xs text-gray-700">
                      {adoptPlan!.untouched.slice(0, 400).map((u) => (
                        <li key={u.crfQuestionId}>
                          {adoptFormLabel({ name: u.formName, code: u.formCode })}:{' '}
                          <span className="font-mono">{u.variable}</span> — {u.text}
                        </li>
                      ))}
                      {adoptPlan!.untouched.length > 400 && (
                        <li className="text-gray-500">
                          …and {adoptPlan!.untouched.length - 400} more.
                        </li>
                      )}
                    </ul>
                  )}
                </div>
              )}

              {rejected.length > 0 && (
                <div className="mb-5 rounded-lg border border-red-200 bg-red-50 p-4">
                  <p className="flex items-center gap-2 text-sm font-semibold text-red-900">
                    <AlertTriangle className="h-4 w-4" />
                    {isAdopt
                      ? `${rejected.length} row(s) cannot be placed — everything else still applies`
                      : 'Nothing will be applied while any row is rejected'}
                  </p>
                  <table className="mt-3 w-full text-sm">
                    <thead>
                      <tr className="text-left text-red-900/70">
                        <th className="py-1 pr-3 font-medium">Row</th>
                        <th className="py-1 pr-3 font-medium">Variable</th>
                        <th className="py-1 font-medium">Why it was rejected</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(expanded.rejected ? rejected : rejected.slice(0, ROW_CAP)).map((r) => (
                        <tr key={`rej-${r.rowNumber}`} className="align-top border-t border-red-200">
                          <td className="py-2 pr-3 tabular-nums text-red-900">{r.rowNumber}</td>
                          <td className="py-2 pr-3 font-mono text-xs text-red-900">{r.fieldName || '—'}</td>
                          <td className="py-2 text-red-900">{r.rejection?.message}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {rejected.length > ROW_CAP && (
                    <button type="button" onClick={() => setExpanded((p) => ({ ...p, rejected: !p.rejected }))}
                      className="mt-2 text-xs font-medium text-red-900 underline">
                      {expanded.rejected ? 'Show fewer' : `Show all ${rejected.length}`}
                    </button>
                  )}
                </div>
              )}

              {/* ── Adopt: what applies without asking ── */}
              {isAdopt && autoRows.length > 0 && (
                <div className="mb-2 rounded-lg border border-green-200 bg-green-50 p-4">
                  <p className="text-sm font-semibold text-green-900">
                    {autoRows.length} row(s) matched the forms exactly, or exactly once case,
                    numbering and punctuation are set aside
                  </p>
                  <button type="button" onClick={() => setExpanded((p) => ({ ...p, auto: !p.auto }))}
                    className="mt-2 text-xs font-medium text-green-900 underline">
                    {expanded.auto ? 'Hide the changes' : 'Show the changes'}
                  </button>
                  {expanded.auto && (
                    <div className="mt-3 overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="text-left text-green-900/70 border-b border-green-200">
                            <th className="py-1 pr-3 font-medium">Row</th>
                            <th className="py-1 pr-3 font-medium">Variable</th>
                            <th className="py-1 pr-3 font-medium">Field</th>
                            <th className="py-1 pr-3 font-medium">Current</th>
                            <th className="py-1 font-medium">Proposed</th>
                          </tr>
                        </thead>
                        <tbody>
                          {autoRows.slice(0, ROW_CAP).flatMap((r) =>
                            r.changes.map((c, i) => (
                              <tr key={`auto-${r.rowNumber}-${c.column}`} className="border-b border-green-100 align-top">
                                <td className="py-1 pr-3 tabular-nums text-green-900/70">{i === 0 ? r.rowNumber : ''}</td>
                                <td className="py-1 pr-3 font-mono text-xs text-green-900">
                                  {i === 0 ? (r.currentVariable || r.fieldName) : ''}
                                  {i === 0 && r.matchedBy && r.matchedBy !== 'field_name' && (
                                    <div className="text-[10px] font-sans text-green-900/70">
                                      matched on {MATCHED_BY_LABEL[r.matchedBy] || r.matchedBy}
                                    </div>
                                  )}
                                </td>
                                <td className="py-1 pr-3 text-green-900">
                                  {COLUMN_LABELS[c.column] || c.column}
                                  {c.target === 'none' && (
                                    <span className="ml-1 text-xs text-green-900/70">(not applied in this mode)</span>
                                  )}
                                  {c.note && <div className="text-xs text-amber-700 mt-0.5">{c.note}</div>}
                                </td>
                                <td className="py-1 pr-3 text-green-900/60 line-through">{c.current || <em>empty</em>}</td>
                                <td className="py-1 text-green-900 font-medium">{c.proposed || <em>empty</em>}</td>
                              </tr>
                            )),
                          )}
                        </tbody>
                      </table>
                      {autoRows.length > ROW_CAP && (
                        <p className="mt-2 text-xs text-green-900/70">
                          Showing the first {ROW_CAP} of {autoRows.length}.
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}

              {changed.length > 0 && (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-gray-500 border-b border-gray-200">
                        <th className="py-2 pr-3 font-medium">Row</th>
                        <th className="py-2 pr-3 font-medium">Variable</th>
                        <th className="py-2 pr-3 font-medium">Field</th>
                        <th className="py-2 pr-3 font-medium">Current</th>
                        <th className="py-2 font-medium">Proposed</th>
                      </tr>
                    </thead>
                    <tbody>
                      {changed.flatMap((r) =>
                        r.changes.map((c: any, i: number) => (
                          <tr key={`${r.rowNumber}-${c.column}`} className="border-b border-gray-100 align-top">
                            <td className="py-2 pr-3 tabular-nums text-gray-500">{i === 0 ? r.rowNumber : ''}</td>
                            <td className="py-2 pr-3 font-mono text-xs text-gray-700">
                              {i === 0 ? r.fieldName : ''}
                            </td>
                            <td className="py-2 pr-3 text-gray-900">
                              {COLUMN_LABELS[c.column] || c.column}
                              {c.appliesTo === 'entry' && (
                                <span className="ml-1 text-xs text-gray-500">(dictionary only)</span>
                              )}
                              {c.target === 'none' && (
                                <span className="ml-1 text-xs text-gray-500">(not applied in this mode)</span>
                              )}
                              {c.note && <div className="text-xs text-amber-700 mt-0.5">{c.note}</div>}
                            </td>
                            <td className="py-2 pr-3 text-gray-500 line-through">{c.current || <em>empty</em>}</td>
                            <td className="py-2 text-gray-900 font-medium">{c.proposed || <em>empty</em>}</td>
                          </tr>
                        )),
                      )}
                    </tbody>
                  </table>
                </div>
              )}

              {isAdopt && adoptApply.storesOnly && (
                <p className="py-8 text-center text-sm text-gray-600">
                  Every row matches the forms as they are. Adopting stores this file as the
                  study&apos;s data dictionary without changing any form.
                </p>
              )}
              {!isAdopt && changed.length === 0 && rejected.length === 0 && (
                <p className="py-8 text-center text-sm text-gray-600">
                  Every row matches the study as it is — there is nothing to apply.
                </p>
              )}
            </>
          )}

          {stage === 'applying' && (
            <div className="py-12 text-center text-gray-600">
              <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary-600" />
              <p className="mt-3 text-sm">Applying the edits…</p>
            </div>
          )}

          {stage === 'done' && isAdopt && adoptResult && (
            <div>
              {/* Store-only: nothing applied to any form, the file only became
                  the study's dictionary. Saying "Adopted onto 0 form(s)" read
                  as a failure. */}
              {!adoptResult.rowsApplied && (adoptResult.formsUpdated || []).length === 0 ? (
                <p className="flex items-center gap-2 text-green-800 font-medium">
                  <CheckCircle2 className="h-5 w-5" />
                  Stored the dictionary ({adoptResult.entriesStored ?? 0} entries). No form changed.
                </p>
              ) : (
                <>
                  <p className="flex items-center gap-2 text-green-800 font-medium">
                    <CheckCircle2 className="h-5 w-5" />
                    Adopted onto {(adoptResult.formsUpdated || []).length} form(s) — {adoptResult.rowsApplied} row(s) applied.
                  </p>
                  {adoptResult.entriesStored !== undefined && (
                    <p className="mt-2 text-sm text-gray-700">
                      {adoptStorageLines(adoptResult)[0]}
                    </p>
                  )}
                </>
              )}
              {/* What was not stored, and which forms' entries were stored
                  without a link to a form (2026-10-01). The first line, the
                  stored count, is already shown above. */}
              {adoptStorageLines(adoptResult).slice(1).map((line) => (
                // A list item (a form, a reason) has no closing full stop or colon.
                !/[.:]$/.test(line) ? (
                  <p key={line} className="ml-4 text-sm text-amber-900">{line}</p>
                ) : (
                  <p key={line} className="mt-2 text-sm text-amber-900">{line}</p>
                )
              ))}
              {/* What the adoption did NOT rewrite, named from the plan the
                  apply ran against. The dictionary is never in this list. */}
              {/* With renames, each document shows a rename notice offering the choices
                  (plan step 2), so this points there. Without renames no notice opens and
                  regenerating is the way to pick up the adoption's other changes. */}
              {(adoptPlan?.precondition.outOfDate || []).length > 0 && (
                <p className="mt-2 text-sm text-amber-900">
                  {(adoptResult.renames || []).length > 0
                    ? `Out of date now: ${joinOutOfDate(adoptPlan!.precondition.outOfDate!)}. Until you act on each one's notice, they reflect the dictionary as it was before this adoption.`
                    : `Regenerate next: ${joinOutOfDate(adoptPlan!.precondition.outOfDate!)}. Until then they reflect the dictionary as it was before this adoption.`}
                </p>
              )}
              {(adoptResult.renames || []).length > 0 && (
                <div className="mt-5 rounded-lg border border-amber-200 bg-amber-50 p-4">
                  <p className="text-sm font-semibold text-amber-900">
                    {(adoptResult.renames || []).length} variable(s) renamed
                  </p>
                  <p className="mt-1 text-xs text-amber-900/80">
                    {(adoptPlan?.precondition.outOfDate || []).length > 0
                      ? `${joinOutOfDate(adoptPlan!.precondition.outOfDate!)} still use the old ` +
                        'names: their mapping rows, check ids and annotations. Each now shows a notice ' +
                        'naming these renames, where you choose how to bring it up to date.'
                      : 'Documents you generate from now on will use these names.'}
                  </p>
                  <ul className="mt-3 space-y-1 text-sm text-amber-900 font-mono">
                    {(adoptResult.renames || []).map((r) => (
                      <li key={r.crfQuestionId}>{r.formCode}: {r.from} → {r.to}</li>
                    ))}
                  </ul>
                </div>
              )}
              {!!adoptResult.questionsAdded && (
                <p className="mt-3 text-sm text-gray-700">
                  {adoptResult.questionsAdded} new question(s) were appended from the dictionary.
                </p>
              )}
              {!!adoptResult.reordersApplied && (
                <p className="mt-3 text-sm text-gray-700">
                  {adoptResult.reordersApplied} section(s) now ask their questions in the dictionary's order.
                </p>
              )}
              {!!adoptResult.repeatBlocksApplied && (
                <p className="mt-3 text-sm text-gray-700">
                  {adoptResult.repeatBlocksApplied} section(s) are now marked as repeating blocks.
                </p>
              )}
              {adoptResult.notApplied && (
                <div className="mt-5 rounded-lg border border-gray-200 bg-gray-50 p-4">
                  <p className="text-sm font-semibold text-gray-800">What was not applied</p>
                  <ul className="mt-2 space-y-1 text-sm text-gray-700">
                    {adoptResult.notApplied.needsReviewDeclined > 0 && (
                      <li>{adoptResult.notApplied.needsReviewDeclined} proposed match(es) you did not accept.</li>
                    )}
                    {adoptResult.notApplied.skipped > 0 && (
                      <li>{adoptResult.notApplied.skipped} row(s) you turned off.</li>
                    )}
                    {adoptResult.notApplied.rejected > 0 && (
                      <li>{adoptResult.notApplied.rejected} row(s) that could not be placed on any form.</li>
                    )}
                    {adoptResult.notApplied.unchanged > 0 && (
                      <li>{adoptResult.notApplied.unchanged} row(s) the forms already agreed with.</li>
                    )}
                    {adoptResult.notApplied.unclaimedQuestions > 0 && (
                      <li>
                        {adoptResult.notApplied.unclaimedQuestions} question(s) had no row in the
                        dictionary and were left exactly as they were.
                      </li>
                    )}
                  </ul>
                </div>
              )}
            </div>
          )}

          {stage === 'done' && !isAdopt && result && (
            <div>
              <p className="flex items-center gap-2 text-green-800 font-medium">
                <CheckCircle2 className="h-5 w-5" />
                Applied {result.rowsApplied} edit(s) — {result.entriesUpdated} dictionary entr(ies),{' '}
                {(result.formsUpdated || []).length} form(s).
              </p>
              {(result.staleDownstream || []).length > 0 && (
                <div className="mt-5 rounded-lg border border-amber-200 bg-amber-50 p-4">
                  <p className="text-sm font-semibold text-amber-900">
                    These documents keep their own copy of what you changed, so they are now out of date
                  </p>
                  <p className="mt-1 text-xs text-amber-900/80">
                    Nothing was regenerated — regenerating is a separate step you choose.
                  </p>
                  <ul className="mt-3 space-y-2">
                    {(result.staleDownstream || []).map((d) => (
                      <li key={d.document} className="text-sm text-amber-900">
                        <strong>{d.document}</strong> — {d.reason}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {stage === 'error' && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-4">
              <p className="flex items-center gap-2 text-sm font-semibold text-red-900">
                <AlertTriangle className="h-4 w-4" /> Nothing was applied
              </p>
              <p className="mt-2 text-sm text-red-900">{error}</p>
            </div>
          )}
        </div>

        <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
          <button onClick={onClose} disabled={busy}
            className="px-4 py-2 text-sm rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50">
            {stage === 'done' ? 'Close' : 'Cancel'}
          </button>
          {stage === 'picking' && (
            <button onClick={runPlan} disabled={!file}
              className="btn-primary px-4 py-2 text-sm flex items-center gap-2 disabled:opacity-50">
              <Upload className="h-4 w-4" /> {copy.cta}
            </button>
          )}
          {stage === 'review' && activePlan && (
            <button
              onClick={requestApply}
              disabled={isAdopt ? !adoptApplyable : !activePlan.applyable}
              title={isAdopt
                ? adoptApply.title
                : (activePlan.applyable
                  ? 'Apply these edits to the forms and the dictionary'
                  : 'Nothing is applied while any row is rejected')}
              className="btn-primary px-4 py-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isAdopt ? adoptApply.label : `Apply ${activePlan.totals.changed} edit(s)`}
            </button>
          )}
          {stage === 'error' && (
            <button onClick={() => { setStage('picking'); setPlan(null); setAdoptPlan(null); setError(''); }}
              className="btn-primary px-4 py-2 text-sm">Choose another file</button>
          )}
        </div>
      </div>
      {confirmDecline && (
        <ConfirmDialog
          title={`Decline ${declinedOnAdopt} proposed ${declinedOnAdopt === 1 ? 'match' : 'matches'}?`}
          confirmLabel="Adopt anyway"
          cancelLabel="Go back and review"
          onCancel={() => setConfirmDecline(false)}
          onConfirm={() => { setConfirmDecline(false); void runApply(); }}
        >
          <p>{declineConfirmText(declinedOnAdopt)}</p>
        </ConfirmDialog>
      )}
    </div>
  );
}
